// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../utils/api';
import ChampRecherche from '../ChampRecherche';
import { filterItems } from '../../utils/recherche';
import {
  NOT_PROVIDED, dateFr, euros, billingErrorMessage, percentBp,
} from '../../utils/formatFacturation';
import { STATUS_DISPLAY, Empty } from './commun';

/**
 * Onglet « Impayés et taux » : factures échues non soldées à une date, avec
 * leurs intérêts moratoires, et taux des intérêts moratoires par semestre.
 */
export default function UnpaidTab({ nomSite: siteName, onOuvrirFacture: onOpenInvoice }) {
  const [au, setAu] = useState('');
  const [data, setData] = useState(null);
  const [rate, setRate] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rateForm, setRateForm] = useState({ annee: String(new Date().getFullYear()), semestre: '01', pourcent: '', source: '' });
  const [rateSending, setSendingRate] = useState(false);
  const [recherche, setRecherche] = useState('');

  const charger = useCallback(async (day) => {
    setLoading(true); setError('');
    try {
      const [i, t] = await Promise.all([
        api.get('/invoices/impayes', { params: day ? { au: day } : {} }),
        api.get('/invoices/taux-moratoires'),
      ]);
      setData(i.data);
      setRate(t.data);
    } catch (e) {
      setError(billingErrorMessage(e, 'Impayés illisibles'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(au); }, [au, charger]);

  // Les totaux du bandeau restent ceux de la situation complète, jamais ceux de
  // la recherche : le restant dû est un chiffre d'exploitation, indépendant du
  // filtre saisi.
  const filteredUnpaid = useMemo(
    () => filterItems(data?.factures || [], recherche,
      (l) => [l.facture?.number, siteName(l.facture?.site_id), STATUS_DISPLAY[l.facture?.status]]),
    [data, recherche, siteName],
  );

  const bp = rateForm.pourcent === '' ? null : Math.round(Number(String(rateForm.pourcent).replace(',', '.')) * 100);
  const rateValid = bp !== null && !Number.isNaN(bp) && bp >= 0 && bp <= 10000 && /^\d{4}$/.test(rateForm.annee);

  const saveRate = async () => {
    setSendingRate(true); setError(''); setSuccess('');
    try {
      const body = { semester_start: `${rateForm.annee}-${rateForm.semestre}-01`, rate_bp: bp };
      if (rateForm.source) body.source = rateForm.source;
      await api.put('/invoices/taux-moratoires', body);
      setSuccess(`Taux du semestre ${body.semester_start} enregistré à ${percentBp(bp)}.`);
      setRateForm((f) => ({ ...f, pourcent: '', source: '' }));
      charger(au);
    } catch (e) {
      setError(billingErrorMessage(e, 'Taux refusé'));
    } finally {
      setSendingRate(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" type="date" label="Situation arrêtée au" InputLabelProps={{ shrink: true }}
          value={au} onChange={(e) => setAu(e.target.value)} helperText="Vide : aujourd'hui" />
        <ChampRecherche
          valeur={recherche} onChange={setRecherche}
          placeholder="Rechercher par numéro ou établissement"
          compte={filteredUnpaid.length} total={data?.factures?.length ?? 0} nomObjet="impayé"
        />
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => charger(au)}>Recharger</Button>
        {data && (
          <Typography variant="body2">
            {data.nombre} facture{data.nombre > 1 ? 's' : ''} en retard,
            {' '}<strong>{euros(data.total_restant_du_cents)}</strong> restant dû (TTC).
          </Typography>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Facture</TableCell>
              <TableCell>Établissement</TableCell>
              <TableCell align="right">Reste dû (TTC)</TableCell>
              <TableCell align="right">Retard</TableCell>
              <TableCell>Intérêts moratoires</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filteredUnpaid.length === 0 ? (
              <TableRow><TableCell colSpan={6}><Empty texte={data?.factures?.length
                ? 'Aucun impayé ne correspond à cette recherche.'
                : "Aucune facture échue non soldée. Les brouillons et les factures annulées n'entrent jamais dans ce décompte."} /></TableCell></TableRow>
            ) : filteredUnpaid.map((l) => {
              const f = l.facture;
              const ex = l.interets ? l.interets.exigibles : undefined;
              return (
                <TableRow key={f.id} hover>
                  <TableCell>
                    <Typography variant="body2">{f.number || 'sans numéro'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {STATUS_DISPLAY[f.status] || f.status}, échéance {dateFr(f.due_date)}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{siteName(f.site_id)}</Typography></TableCell>
                  <TableCell align="right">{euros(f.outstanding_cents)}</TableCell>
                  <TableCell align="right">{l.jours_de_retard} j</TableCell>
                  <TableCell>
                    {ex === true && (
                      <Typography variant="body2">
                        {euros(l.interets.total_cents)}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}(dont {euros(l.interets.indemnite_cents)} d&apos;indemnité, taux {percentBp(l.interets.taux_bp)})
                        </Typography>
                      </Typography>
                    )}
                    {ex === false && (
                      <Typography variant="body2" color="text.secondary">
                        Non exigibles : {l.interets.motif}
                      </Typography>
                    )}
                    {(ex === null || ex === undefined) && (
                      <Typography variant="body2" color="warning.main">
                        Taux du semestre inconnu{l.interets?.motif ? ` : ${l.interets.motif}` : ''}. Aucun montant
                        n&apos;est avancé tant que le taux n&apos;est pas saisi ci dessous.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => onOpenInvoice(f.id)}>Ouvrir</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Taux des intérêts moratoires, par semestre</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {rate?.rappel || 'Le taux change tous les six mois et n\'est jamais écrit dans le code.'}
          {rate && ` Indemnité forfaitaire de recouvrement rendue par l'API : ${euros(rate.indemnite_recouvrement_cents)}.`}
        </Typography>

        {rate && rate.taux.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Aucun taux n&apos;est enregistré. Ce n&apos;est pas une panne : c&apos;est
            l&apos;état normal d&apos;un déploiement neuf, et c&apos;est ce qui empêche
            de chiffrer les intérêts ci dessus.
          </Alert>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={2}>
            <TextField fullWidth size="small" label="Année" value={rateForm.annee}
              onChange={(e) => setRateForm((f) => ({ ...f, annee: e.target.value }))} />
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Semestre</InputLabel>
              <Select value={rateForm.semestre} label="Semestre"
                onChange={(e) => setRateForm((f) => ({ ...f, semestre: e.target.value }))}>
                <MenuItem value="01">1er janvier</MenuItem>
                <MenuItem value="07">1er juillet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth size="small" label="Taux (%)" value={rateForm.pourcent}
              onChange={(e) => setRateForm((f) => ({ ...f, pourcent: e.target.value }))}
              helperText={rateForm.pourcent === '' ? 'Taux directeur majoré de huit points' : `Sera envoyé : ${bp} points de base`} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth size="small" label="Source" value={rateForm.source}
              onChange={(e) => setRateForm((f) => ({ ...f, source: e.target.value }))}
              helperText="Publication officielle qui fait foi" />
          </Grid>
          <Grid item xs={1}>
            <Button variant="contained" size="small" disabled={!rateValid || rateSending} onClick={saveRate}>
              {rateSending ? <CircularProgress size={16} /> : 'Poser'}
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 2 }}>
          Poser un semestre déjà renseigné écrase la valeur existante, sans avertissement de l&apos;API.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Semestre</TableCell>
              <TableCell align="right">Taux</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rate?.taux || []).length === 0 ? (
              <TableRow><TableCell colSpan={3}><Empty texte="Aucun taux enregistré." /></TableCell></TableRow>
            ) : rate.taux.map((t) => (
              <TableRow key={t.semester_start}>
                <TableCell>{dateFr(t.semester_start)}</TableCell>
                <TableCell align="right">{percentBp(t.rate_bp)}</TableCell>
                <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{t.source || NOT_PROVIDED}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
